import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { resolveColumnEdit, type DataTableColumn } from "@/components/table/columns";
import {
  cellKey,
  cellsPendingReconnectRetry,
  reduceCellEdit,
  resolveEditCommit,
  splitCellKey,
  type CellEditState,
  type EditCommitOutcome,
  type EditCommitPayload,
} from "@/lib/inline-edit";
import { createSerialQueue } from "@/lib/serial-queue";
import { useOnlineStatus } from "@/lib/use-online-status";

export interface InlineEditCommitArgs<Row> {
  row: Row;
  column: DataTableColumn<Row>;
  payload: EditCommitPayload;
}

export type EditConflictResolution = "mine" | "theirs";

export interface InlineEditConflictArgs<Row> {
  row: Row;
  column: DataTableColumn<Row>;
  resolution: EditConflictResolution;
  /** The value the user had typed/selected before the conflict was refused. */
  myValue: string;
}

export interface UseInlineEditsOptions<Row extends { id: string }> {
  rows: Row[];
  columns: DataTableColumn<Row>[];
  onCommit: (args: InlineEditCommitArgs<Row>) => Promise<EditCommitOutcome>;
  /**
   * "Keep mine" re-sends with a freshly re-fetched `expected_updated_at`
   * (the caller's job — it owns the query); "Keep theirs" discards the
   * local edit and re-fetches so the row shows the current server value.
   * Absent only when no column on this table carries `edit`.
   */
  onResolveConflict?: (args: InlineEditConflictArgs<Row>) => Promise<EditCommitOutcome>;
}

/**
 * The React wiring around `lib/inline-edit.ts`'s pure state machine — the
 * real async call, `navigator.onLine`, and the single-edit reconnect retry
 * (VC-2.3-09; VC-2.3-10's "no queue" holds because this reads only
 * `cellStates`, an in-memory `useState`, never anything persisted). Kept
 * thin on purpose, the same split `use-loading-phase.ts` already
 * establishes: every *decision* this hook makes was already unit-tested in
 * `inline-edit.test.ts` as a pure function; this file is only the plumbing,
 * proven live in a real browser rather than by a DOM test this project has
 * no dependency to run (see the discharge report).
 *
 * `DataTable` owns exactly one instance of this per table. The commit and
 * conflict callbacks are the caller's (a route's) job — this hook, like
 * `DataTable` itself, never knows which endpoint it is talking to.
 */
export function useInlineEdits<Row extends { id: string }>({
  rows,
  columns,
  onCommit,
  onResolveConflict,
}: UseInlineEditsOptions<Row>) {
  const [cellStates, setCellStates] = useState<Record<string, CellEditState>>({});
  const [liveMessage, setLiveMessage] = useState("");
  const isOnline = useOnlineStatus();
  // One instance for the lifetime of this table (review finding 3): every
  // network commit for a given `row.id` is enqueued through this, so a
  // second edit to a row whose first edit is still in flight never starts
  // its own request until the first one has fully resolved — including
  // whatever the caller's `onCommit` does with the response (`UsersList`
  // records the row's freshly-confirmed `updated_at` there) — closing the
  // self-conflict race a fast localhost round trip otherwise hides.
  const enqueueForRow = useMemo(() => createSerialQueue<string>(), []);
  const wasOnlineRef = useRef(isOnline);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const columnsRef = useRef(columns);
  columnsRef.current = columns;
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;
  const resolveRef = useRef(onResolveConflict);
  resolveRef.current = onResolveConflict;
  // `runCommit`/`resolveConflict` read the *latest* cell-state map through
  // this ref rather than a `cellStates` dependency, so their identity stays
  // stable across renders while never resolving against a stale snapshot.
  const cellStatesRef = useRef(cellStates);
  cellStatesRef.current = cellStates;

  const applyOutcome = useCallback((key: string, outcome: EditCommitOutcome) => {
    if (outcome.ok) {
      const { states, liveMessage: message } = reduceCellEdit(cellStatesRef.current, { type: "succeeded", key });
      setCellStates(states);
      setLiveMessage(message);
      return;
    }
    if (outcome.conflict) {
      const { states, liveMessage: message } = reduceCellEdit(cellStatesRef.current, {
        type: "conflict",
        key,
        message: outcome.message,
        currentValue: outcome.currentValue,
      });
      setCellStates(states);
      setLiveMessage(message);
      return;
    }
    const { states, liveMessage: message } = reduceCellEdit(cellStatesRef.current, {
      type: "failed",
      key,
      message: outcome.message,
    });
    setCellStates(states);
    setLiveMessage(message);
  }, []);

  const applyThrown = useCallback((key: string, rawValue: string) => {
    // A thrown error mid-request (a network drop, going offline while the
    // request was already in flight) is never a silent discard
    // (VC-2.3-06) — it lands as "offline" if the browser now agrees we are
    // offline, otherwise as an ordinary retryable failure.
    const nowOffline = typeof navigator !== "undefined" && !navigator.onLine;
    const { states, liveMessage: message } = reduceCellEdit(
      cellStatesRef.current,
      nowOffline ? { type: "offline", key, value: rawValue } : { type: "failed", key, message: undefined },
    );
    setCellStates(states);
    setLiveMessage(nowOffline ? "" : message);
  }, []);

  const runCommit = useCallback(
    async (row: Row, column: DataTableColumn<Row>, rawValue: string): Promise<EditCommitOutcome> => {
      const key = cellKey(row.id, column.key);
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        setCellStates((prev) => reduceCellEdit(prev, { type: "offline", key, value: rawValue }).states);
        // Not a failure the reconnect-retry logic below needs to hear about
        // as one (it already watches `cellStates`' own "offline" entries),
        // but a caller awaiting the outcome directly (Story 15.5's
        // auto-save-on-leave, the one caller that does) must never be told
        // this landed — it did not, yet.
        return { ok: false, message: "You're offline. This will retry once you're back online." };
      }

      // Optimistic UI (VC-2.3-04) happens immediately, never behind the
      // per-row queue below — only the network call itself waits.
      setCellStates((prev) => reduceCellEdit(prev, { type: "start", key, value: rawValue }).states);
      setLiveMessage("");

      return enqueueForRow(row.id, async () => {
        try {
          // Story 15.16: `column.edit` may be a function of the row — the
          // non-null assertion holds exactly as it always did, since a
          // commit is only ever reachable for a cell that resolved to a
          // real config in the first place (`DataTable.tsx`'s own
          // `editableCell`/`identifierCell` gate on that before this is
          // ever called).
          const payload = resolveEditCommit(resolveColumnEdit(column, row)!, row, rawValue);
          const outcome = await commitRef.current({ row, column, payload });
          applyOutcome(key, outcome);
          return outcome;
        } catch (error) {
          applyThrown(key, rawValue);
          const message = error instanceof Error ? error.message : undefined;
          return message === undefined ? { ok: false } : { ok: false, message };
        }
      });
    },
    [applyOutcome, applyThrown, enqueueForRow],
  );

  const commit = useCallback(
    (row: Row, column: DataTableColumn<Row>, rawValue: string) => {
      void runCommit(row, column, rawValue);
    },
    [runCommit],
  );

  const retry = useCallback(
    (row: Row, column: DataTableColumn<Row>) => {
      const state = cellStates[cellKey(row.id, column.key)];
      if (state === undefined) {
        return;
      }
      void runCommit(row, column, state.value);
    },
    [cellStates, runCommit],
  );

  const resolveConflict = useCallback(
    async (row: Row, column: DataTableColumn<Row>, resolution: EditConflictResolution) => {
      const key = cellKey(row.id, column.key);
      const state = cellStatesRef.current[key];
      const handler = resolveRef.current;
      if (state === undefined || handler === undefined) {
        return;
      }

      if (resolution === "theirs") {
        setCellStates((prev) => reduceCellEdit(prev, { type: "discarded", key }).states);
        setLiveMessage("");
        // Still goes through the row's own queue: a "Keep theirs" refetch
        // racing an unrelated in-flight commit for the same row is the same
        // shape of race finding 3 closed for ordinary commits.
        await enqueueForRow(row.id, async () => {
          try {
            await handler({ row, column, resolution, myValue: state.value });
          } catch {
            // The refresh itself failing leaves the cell at its (already
            // cleared) resting state, showing whatever the cache still has
            // — there is no local edit left to protect at this point.
          }
        });
        return;
      }

      setCellStates((prev) => reduceCellEdit(prev, { type: "start", key, value: state.value }).states);
      setLiveMessage("");
      await enqueueForRow(row.id, async () => {
        try {
          const outcome = await handler({ row, column, resolution, myValue: state.value });
          applyOutcome(key, outcome);
        } catch {
          applyThrown(key, state.value);
        }
      });
    },
    [applyOutcome, applyThrown, enqueueForRow],
  );

  // Reconnect: retry every cell still "offline" the instant `online` fires
  // (VC-2.3-09), without further user action. Looks only at `cellStates` —
  // nothing read from any persisted browser storage (VC-2.3-10's "no queue").
  useEffect(() => {
    if (!wasOnlineRef.current && isOnline) {
      const pending = cellsPendingReconnectRetry(cellStates);
      for (const key of pending) {
        const { rowId, columnKey } = splitCellKey(key);
        const row = rowsRef.current.find((candidate) => candidate.id === rowId);
        const column = columnsRef.current.find((candidate) => candidate.key === columnKey);
        const state = cellStates[key];
        if (row !== undefined && column !== undefined && state !== undefined) {
          void runCommit(row, column, state.value);
        }
      }
    }
    wasOnlineRef.current = isOnline;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  return {
    cellStates,
    liveMessage,
    isOnline,
    commit,
    // The awaitable twin `commit` above deliberately discards (`void
    // runCommit(...)`) — every ordinary blur/Enter/Tab commit only ever
    // needed `cellStates` to reflect the outcome, never the outcome value
    // itself. Story 15.5's auto-save-on-leave is the one caller that does:
    // by the time this resolves, the field itself may already be unmounted
    // (the whole point), so its own resting/error state has nothing left to
    // render into — the caller needs the real `EditCommitOutcome` back to
    // decide which toast to show instead.
    commitAwaitable: runCommit,
    retry,
    resolveConflict,
  };
}
